/**
 * DRI Summary Info Cards – Employee DRI Status, Top 3 Products, and Certifications metrics.
 * Used at the top of DRI Insights when data is loaded.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SEARCE_COLORS } from '../../theme';
import MetricCard from './MetricCard';

export interface SummaryMetrics {
  totalActiveEmployees: number;
  driActiveEmployees: number;
  driInactiveEmployees: number;
  topThreeProducts: Array<{ productName: string; averageDRI: number }>;
  profilesWithNoProjectsDRI: number;
  profilesWithNoCertificationDRI: number;
  profilesWithNoTechnicalSkillDRI: number;
}

export interface CertificationMetrics {
  activeCertifications: number;
  expiringCertifications: number;
  employeesWithoutCertifications: number;
}

export interface DRISummaryCardsProps {
  summaryMetrics: SummaryMetrics;
  certificationMetrics: CertificationMetrics;
  showDriProfilesWithout: boolean;
  onToggleDriProfilesWithout: () => void;
  onUnregisteredClick: () => void;
}

const DRISummaryCards: React.FC<DRISummaryCardsProps> = ({
  summaryMetrics,
  certificationMetrics,
  showDriProfilesWithout: _showDriProfilesWithout,
  onToggleDriProfilesWithout: _onToggleDriProfilesWithout,
  onUnregisteredClick,
}) => {
  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: 1.5,
        }}
      >
        {/* Group 1: Employee DRI Status */}
        <Box
          sx={{
            flex: 1,
            p: 1.5,
            border: `1px solid ${SEARCE_COLORS.neutral_200}`,
            borderRadius: 2,
            backgroundColor: alpha(SEARCE_COLORS.primary, 0.02),
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: SEARCE_COLORS.textPrimary,
              fontSize: '0.95rem',
              mb: 1.5,
              textAlign: 'center',
              letterSpacing: '0.02em',
            }}
          >
            Employee DRI Status
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <MetricCard
                title="Total Active"
                value={summaryMetrics.totalActiveEmployees.toLocaleString()}
                color={SEARCE_COLORS.primary}
                tooltip="Active employees included in the current DRI Insights filters (organization scope)."
              />
            </Box>
            {/*
            Registered Profiles (interactive toggle for “DRI Profiles without” below) — disabled
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                position: 'relative',
                cursor: 'pointer',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: SEARCE_COLORS.success,
                  border: `2px solid ${SEARCE_COLORS.white}`,
                  boxShadow: `0 2px 4px ${alpha(SEARCE_COLORS.success, 0.3)}`,
                  zIndex: 10,
                  animation: showDriProfilesWithout ? 'pulse 2s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                    '50%': { opacity: 0.7, transform: 'scale(1.1)' },
                  },
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: 3,
                  border: showDriProfilesWithout
                    ? `2px solid ${SEARCE_COLORS.success}`
                    : `1px solid ${alpha(SEARCE_COLORS.success, 0.3)}`,
                  zIndex: 1,
                  pointerEvents: 'none',
                  transition: 'all 0.3s ease',
                },
              }}
              onClick={onToggleDriProfilesWithout}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  zIndex: 0,
                }}
              >
                <MetricCard
                  title="Registered Profiles"
                  value={summaryMetrics.driActiveEmployees.toLocaleString()}
                  color={SEARCE_COLORS.success}
                />
              </Box>
            </Box>
            */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <MetricCard
                title="Registered Profiles"
                value={summaryMetrics.driActiveEmployees.toLocaleString()}
                color={SEARCE_COLORS.success}
                tooltip="Employees with a DRI profile row (registered in the DRI dataset) for the selected scope."
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <MetricCard
                title="Unregistered Profiles"
                value={summaryMetrics.driInactiveEmployees.toLocaleString()}
                color={SEARCE_COLORS.error}
                onClick={onUnregisteredClick}
                tooltip="Active employees without a DRI profile row. Click to open the unregistered list when that flow is enabled."
              />
            </Box>
          </Box>

          {/*
          {showDriProfilesWithout && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                border: `1px solid ${SEARCE_COLORS.neutral_200}`,
                borderRadius: 2,
                backgroundColor: alpha(SEARCE_COLORS.error, 0.02),
                display: 'flex',
                flexDirection: 'column',
                animation: 'slideDown 0.3s ease-out',
                '@keyframes slideDown': {
                  from: { opacity: 0, transform: 'translateY(-10px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: SEARCE_COLORS.textPrimary,
                  fontSize: '0.95rem',
                  mb: 1.5,
                  textAlign: 'center',
                  letterSpacing: '0.02em',
                }}
              >
                DRI Profiles without
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <MetricCard
                    title="Projects deployed"
                    value={summaryMetrics.profilesWithNoProjectsDRI.toLocaleString()}
                    color={SEARCE_COLORS.error}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <MetricCard
                    title="Technical Certification"
                    value={summaryMetrics.profilesWithNoCertificationDRI.toLocaleString()}
                    color={SEARCE_COLORS.error}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <MetricCard
                    title="Technical Skills"
                    value={summaryMetrics.profilesWithNoTechnicalSkillDRI.toLocaleString()}
                    color={SEARCE_COLORS.error}
                  />
                </Box>
              </Box>
            </Box>
          )}
          */}
        </Box>

        {/* Group 2: Top 3 Products */}
        <Box
          sx={{
            flex: 1,
            p: 1.5,
            border: `1px solid ${SEARCE_COLORS.neutral_200}`,
            borderRadius: 2,
            backgroundColor: alpha(SEARCE_COLORS.info, 0.02),
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: SEARCE_COLORS.textPrimary,
              fontSize: '0.95rem',
              mb: 1.5,
              textAlign: 'center',
              letterSpacing: '0.02em',
            }}
          >
            Top 3 Products
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
            {summaryMetrics.topThreeProducts.length > 0 ? (
              summaryMetrics.topThreeProducts.map((product) => (
                <Box key={product.productName} sx={{ flex: 1, minWidth: 0 }}>
                  <MetricCard
                    title={product.productName}
                    value={product.averageDRI.toFixed(2)}
                    color={SEARCE_COLORS.info}
                    tooltip={`Average DRI score for product “${product.productName}” across included profiles.`}
                  />
                </Box>
              ))
            ) : (
              <>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <MetricCard
                    title="Product 1"
                    value="N/A"
                    color={SEARCE_COLORS.info}
                    tooltip="Shown when there is no top product data for the current filters."
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <MetricCard
                    title="Product 2"
                    value="N/A"
                    color={SEARCE_COLORS.info}
                    tooltip="Shown when there is no top product data for the current filters."
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <MetricCard
                    title="Product 3"
                    value="N/A"
                    color={SEARCE_COLORS.info}
                    tooltip="Shown when there is no top product data for the current filters."
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Group 3: Certifications */}
        <Box
          sx={{
            flex: 1,
            p: 1.5,
            border: `1px solid ${SEARCE_COLORS.neutral_200}`,
            borderRadius: 2,
            backgroundColor: alpha(SEARCE_COLORS.info, 0.02),
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: SEARCE_COLORS.textPrimary,
              fontSize: '0.95rem',
              mb: 1.5,
              textAlign: 'center',
              letterSpacing: '0.02em',
            }}
          >
            Certifications
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <MetricCard
                title="Active Certifications"
                value={certificationMetrics.activeCertifications.toLocaleString()}
                color={SEARCE_COLORS.success}
                tooltip="Certifications currently in an active state for employees in scope."
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <MetricCard
                title="Expiring Certifications"
                value={certificationMetrics.expiringCertifications.toLocaleString()}
                color={SEARCE_COLORS.warning}
                tooltip="Certifications approaching expiry within the window used by DRI Insights."
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <MetricCard
                title="Employees without Certifications"
                value={certificationMetrics.employeesWithoutCertifications.toLocaleString()}
                color={SEARCE_COLORS.error}
                tooltip="Employees in scope who do not have any recorded certification."
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DRISummaryCards;
